import React, { useState } from "react";
import {
  ArrowLeft,
  CalendarDays,
  ImagePlus,
  MapPin,
  Ticket,
  PlusCircle,
  Settings,
  Trash2,
  BadgeDollarSign,
} from "lucide-react";
import ManageEvents from "./ManageEvents";

export default function CreateEvent({ onBack, userEmail }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [image, setImage] = useState(null);
  const [showManage, setShowManage] = useState(false);
  const [description, setDescription] = useState("");
  const [customUrl, setCustomUrl] = useState("");

  const [ticketTiers, setTicketTiers] = useState([
    { title: "General Admission", price: "", quantity: "" },
  ]);

  if (showManage) {
    return (
      <ManageEvents
        userEmail={userEmail}
        onBack={() => setShowManage(false)}
      />
    );
  }

  const updateTier = (index, field, value) => {
    setTicketTiers((prev) =>
      prev.map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      )
    );
  };

  const addTier = () => {
    setTicketTiers((prev) => [
      ...prev,
      { title: "", price: "", quantity: "" },
    ]);
  };

  const removeTier = (index) => {
    setTicketTiers((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };


  const formatCustomUrl = (value) => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanedTiers = ticketTiers
      .map((tier) => ({
        title: tier.title.trim(),
        price: Number(tier.price),
        quantity: Number(tier.quantity),
      }))
      .filter(
        (tier) =>
          tier.title &&
          !Number.isNaN(tier.price) &&
          !Number.isNaN(tier.quantity) &&
          tier.quantity > 0 &&
          tier.price >= 0
      );

    if (!cleanedTiers.length) {
      alert("Please add at least one valid ticket tier.");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("location", location);
    formData.append("dateTime", dateTime);
    formData.append("createdBy", userEmail);
    formData.append("description", description)
    formData.append("ticketTiers", JSON.stringify(cleanedTiers));
    formData.append("customUrl", customUrl);

    if (image) formData.append("image", image);

    try {
      const response = await fetch("/api/createEvent", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        alert("Event created!");
        onBack();
      } else {
        alert("Error creating event.");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to create event.");
    }
  };

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="mt-4 max-w-2xl">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Create Event
            </h1>
            <p className="mt-3 text-base leading-7 text-slate-600">
              Publish a new event, upload a poster image, and create one or more
              ticket tiers like general admission, VIP, or weekend passes.
            </p>
          </div>
        </header>

        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50/80 px-6 py-5 sm:px-8">
              <h2 className="text-lg font-semibold text-slate-900">
                Event details
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Fill out the information below to create your event listing.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-6 px-6 py-6 sm:px-8"
              encType="multipart/form-data"
            >
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Event Name
                </label>
                <div className="relative">
                  <PlusCircle className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    placeholder="Enter event name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Event Description
                </label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="min-h-[120px] w-full resize-y rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    placeholder="Enter event description"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Custom Event URL
                  </label>

                  <input
                    type="text"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(formatCustomUrl(e.target.value))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    placeholder="summer-kickoff"
                  />

                  <p className="mt-2 text-xs text-slate-500">
                    Your event page will be available at /events/{customUrl || "custom-url"}.
                    Use lowercase letters, numbers, and dashes only.
                  </p>
                </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Location
                </label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    placeholder="Enter venue or location"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Date and Time
                </label>
                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="datetime-local"
                    value={dateTime}
                    onChange={(e) => setDateTime(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Poster Image
                </label>
                <div className="relative rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 transition hover:border-slate-400 hover:bg-slate-100/60">
                  <div className="pointer-events-none mb-3 flex items-center gap-2 text-sm text-slate-600">
                    <ImagePlus className="h-4 w-4 text-slate-400" />
                    Upload an optional event poster or promotional image
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImage(e.target.files[0])}
                    className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800"
                  />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">
                      Ticket Tiers
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Add different ticket options like VIP, all-day, or weekend
                      passes.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={addTier}
                    className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <PlusCircle className="h-4 w-4" />
                    Add Tier
                  </button>
                </div>

                <div className="space-y-4">
                  {ticketTiers.map((tier, index) => (
                    <div
                      key={index}
                      className="rounded-3xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="mb-4 flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-900">
                          Tier {index + 1}
                        </p>

                        {ticketTiers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTier(index)}
                            className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-red-600 ring-1 ring-slate-200 transition hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">
                            Tier Title
                          </label>
                          <div className="relative">
                            <Ticket className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              value={tier.title}
                              onChange={(e) =>
                                updateTier(index, "title", e.target.value)
                              }
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                              placeholder="General Admission"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                              Price
                            </label>
                            <div className="relative">
                              <BadgeDollarSign className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={tier.price}
                                onChange={(e) =>
                                  updateTier(index, "price", e.target.value)
                                }
                                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                                placeholder="25.00"
                                required
                              />
                            </div>
                          </div>

                          <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                              Quantity
                            </label>
                            <div className="relative">
                              <Ticket className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <input
                                type="number"
                                min="1"
                                value={tier.quantity}
                                onChange={(e) =>
                                  updateTier(index, "quantity", e.target.value)
                                }
                                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                                placeholder="100"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-6 sm:flex-row">
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 sm:w-auto"
                >
                  <PlusCircle className="h-4 w-4" />
                  Create Event
                </button>

                <button
                  type="button"
                  onClick={() => setShowManage(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 sm:w-auto"
                >
                  <Settings className="h-4 w-4" />
                  Manage My Events
                </button>

                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200 sm:ml-auto sm:w-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              </div>
            </form>
          </section>

          <aside className="space-y-6">
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50/80 px-6 py-5">
                <h3 className="text-lg font-semibold text-slate-900">
                  Publishing tips
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  A few quick things that help events look more polished.
                </p>
              </div>

              <div className="space-y-4 px-6 py-6">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">
                    Use clear ticket names
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Titles like General Admission, VIP, Friday Pass, or Weekend
                    Pass make checkout easier to understand.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">
                    Set inventory by tier
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Each ticket tier can have its own quantity, so you can limit
                    VIP or special-access passes separately.
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">
                    Offer flexible pricing
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Multi-tier tickets are perfect for one-day, all-day,
                    weekend, and premium entry options.
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white shadow-sm">
              <p className="text-sm font-medium text-white/70">
                Event management
              </p>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight">
                Need to update an existing event?
              </h3>
              <p className="mt-3 text-sm leading-6 text-white/75">
                Jump straight into your dashboard to edit event details, manage
                tickets, or scan entries at the door.
              </p>

              <button
                type="button"
                onClick={() => setShowManage(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
              >
                <Settings className="h-4 w-4" />
                Open Manage Events
              </button>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}